import styles from "./MSSlotCalendar.module.css";
import {
    Button,
    Calendar,
    Col, DatePicker, Descriptions,
    Dropdown,
    Modal,
    notification,
    Result,
    Row,
    Select, Spin,
    Table,
} from "antd";
import {
    DEFAULT_THROTTLE, FUEL_TYPE_CATEGORY_ID_STR,
    MasterScheduleEvent,
    MONTHS,
    MotorFamily,
    PAGINATION_MAX_PAGE_SIZE,
    ProductionLine, ScheduledTruck,
    ShortTimeFormat, Truck, TruckDateFilterType, TruckFilter,
} from "../../api/models";
import dayjs, {Dayjs} from "dayjs";
import {LeftOutlined, RightOutlined, TruckOutlined, MoreOutlined} from "@ant-design/icons";
import MasterScheduleEventButtonModal from "./MasterScheduleEventButtonModal";
import {
    getMotorFamilyLineDetail,
    ProductionLineDetail,
    useMasterScheduleSlotsContext
} from "../../contexts/MasterScheduleSlotsContext";
import Utils from "../../util/util";
import React, {useCallback, useContext, useState} from "react";
import SidePanel from "../../components/widgets/SidePanel";
import Title from "antd/lib/typography/Title";
import {ArrayParam, StringParam, useQueryParam} from "use-query-params";
import useCheckMobileScreen from "../../hook/useCheckMobileScreen";
import useTruckReport from "../../swr/useTruckReport";
import MSTruckScheduleModal, {DEFAULT_RTS_OFFSET, DEFAULT_RTS_SHIP_OFFSET, SlotView} from "./MSTruckScheduleModal";
import TruckReportFilterControls from "../../components/Filter/TruckReportFilterControls";
import {throttle} from "lodash";
import BMButton, {BMButtonProps} from "../../components/BMButton";
import useMasterScheduleFlags from "../../swr/useMasterScheduleFlags";
import {TruckReportDto} from "../reports/truckReport";
import {MenuItemType} from "antd/es/menu/interface";
import {ConfiguratorContext} from "../../context";
import {useIntl} from "react-intl";
import descriptionStyles from "../../styles/DescriptionStyle.module.css";

const years = Array.from({ length: 20 }, (_, i) => dayjs().year() - 10 + i);


const MSSlotCalendar = (props: {
    onChangeEvent: (e?:MasterScheduleEvent | undefined) => void;
    onDateChange: (date: Dayjs) => void;
    onSelectSlot: (p: ProductionLineDetail, sn: number | undefined) => void;
    isReadOnly: boolean;
} ) => {

    const {
        onDateChange,
    } = props;

    const msContext = useMasterScheduleSlotsContext();
    const { selectedProductionLines, lineWithEventsAsync, getProductionLineDetail} = msContext;

    const [searchFilterParam, setSearchFilterParam] = useQueryParam<string|undefined|null>("filter", StringParam);
    const [dealerFilterParam, setDealerFilterParam] = useQueryParam<any>("dealer", ArrayParam);
    const [entityTypeFilterParam, setEntityTypeFilterParam] = useQueryParam<any>("entityTypeList", ArrayParam);
    const [filter, setFilter] = useState<TruckFilter>({
        search: searchFilterParam || undefined,
        dealerLst: dealerFilterParam || undefined, //silly fix for null
    });


    const isMobile = useCheckMobileScreen( 1300 );
    const [sidePanelKeyParam, setSidePanelKeyParam] = useQueryParam<string | undefined | null >("side", StringParam);
    const defaultSidePanelKey = sidePanelKeyParam || undefined;
    const [activeSidePanelKey, setActiveSidePanelKey] = useState<string | undefined>( isMobile ? undefined : defaultSidePanelKey);

    const isFilterEmpty = !Object.values(filter).filter(v=>!!v?.length).length;
    const filteredTrucksAsync = useTruckReport({
        ...filter,
        dateFilterStart: filter.dateFilterStart?.toDate(),
        dateFilterEnd: filter.dateFilterEnd?.toDate(),
        dateFilterExcludeNull: true,
        isActiveEngineering: true,
        isActive: true,
        disabled: isFilterEmpty,
        page: 0,
        size: PAGINATION_MAX_PAGE_SIZE,
        sort: {
            field: "serialNumber",
            direction: "asc"
        }
    })

    const selectedMonth = msContext.selectedDate?.startOf('month');
    const nextMonth = selectedMonth?.add(1, "month");
    const masterScheduleFlagsAsync = useMasterScheduleFlags({
        isUnscheduled: false,
        isActiveEngineering: true,
        isActive: true,
        dateFilterType: TruckDateFilterType.PRODUCTION,
        dateFilterStart: selectedMonth?.subtract(1, 'week').toDate(),
        dateFilterEnd: nextMonth?.add(1, 'week').toDate(),
        dateFilterExcludeNull: true,
        page: 0,
        size: PAGINATION_MAX_PAGE_SIZE,
    })
    const unscheduledFlagsAsync = useMasterScheduleFlags({
        isUnscheduled: true,
        isActiveEngineering: true,
        isActive: true,
        page: 0,
        size: PAGINATION_MAX_PAGE_SIZE,
    })

    const unscheduledTrucksAsync = useTruckReport({
        isUnscheduled: true,
        isActiveEngineering: true,
        isActive: true,
        page: 0,
        size: PAGINATION_MAX_PAGE_SIZE,
        sort: {
            field: "serialNumber",
            direction: "asc"
        }
    })

    msContext.filteredTrucks = filteredTrucksAsync.data?.content;
    msContext.unscheduledTrucks = unscheduledTrucksAsync.data?.content
    msContext.masterScheduleFlags = [ ...(masterScheduleFlagsAsync.data?.content || []), ...(unscheduledFlagsAsync.data?.content || []) ].sort( (a, b) => Number(a.serialNumber) - Number(b.serialNumber) );

    const handleTabClick = (key: string | undefined) => {
        setActiveSidePanelKey(key);
        setSidePanelKeyParam(key)
    }

    const handleChangeTruck = (t: Truck) => {
        unscheduledTrucksAsync.mutate();
        filteredTrucksAsync.mutate();
        props.onChangeEvent?.();
    }

    const handleFilterChange = useCallback( throttle( (_values: Record<string, any>, filter: TruckFilter ) => {
        setSearchFilterParam(filter.search);
        setDealerFilterParam( filter.dealerLst );

        setFilter(filter);
    }, DEFAULT_THROTTLE ), [] );

    const resetQueryParam = () => {
        !!searchFilterParam && setSearchFilterParam(undefined);
        !!dealerFilterParam?.length && setDealerFilterParam( undefined );
        !!entityTypeFilterParam && setEntityTypeFilterParam(undefined);
    }

    const selectedDate = msContext.selectedDate || dayjs();

    const cellRender = (current: Dayjs, info: any) => {
//console.log( info)
        const[firstWeekStart, lastWeekEnd] = Utils.getMonthStartAndEnd(selectedDate);
        const outsideMonth = current.isBefore(firstWeekStart) || current.isAfter(lastWeekEnd);
        const readOnly = props.isReadOnly || outsideMonth;

        if (outsideMonth) {
            return <div style={{ display: "none" }} />; // Skip rendering for extra days
        }

        const isAllLines = !selectedProductionLines?.length
        const details = ( isAllLines
            ? Object.values(ProductionLine)
            : selectedProductionLines )
            ?.map(l => getProductionLineDetail?.(current, l) );

        const summary = details
            .reduce((acc, d) => {
                if ( !acc ) return acc;
                if ( !d ) return acc;

                acc.bevLineOpen += d.bevLineOpen;
                acc.iceLineOpen += d.iceLineOpen;
                acc.iceSerials.push(...d.iceSerials);
                acc.bevSerials.push(...d.bevSerials);
                acc.firstTaktEvent = acc.firstTaktEvent || d.firstTaktEvent;
                acc.iceTaktRate += d.firstTaktEvent?.iceTaktRate || 0;
                acc.bevTaktRate += d.firstTaktEvent?.bevTaktRate || 0;
                return acc;
            }, {
                bevLineOpen: 0,
                iceLineOpen: 0,
                iceSerials: new Array<ScheduledTruck>(),
                bevSerials: new Array<ScheduledTruck>(),
                iceTaktRate: 0,
                bevTaktRate: 0,
            } as ProductionLineDetail);

        const isHoliday = !(!!summary?.iceTaktRate || !!summary?.bevTaktRate || !!summary?.bevSerials.length || !!summary?.iceTaktRate);
        if (isHoliday )  {
            return <div className={styles["date-cell"]} key={`${current.format(ShortTimeFormat)}-cell`} style={{alignContent: "baseline"}}>
                <div className={styles["date-area"]}>{current.date()}</div>
                <div key={`${current.format("YYYY-MM-DD")}-event-name`}
                     className={styles["event-name"]} >
                    <MasterScheduleEventButtonModal
                        type="text"
                        style={{color: "inherit", padding: 0, fontSize: 20}}
                        disabled={readOnly}
                        startDate={current}
                        event={summary?.firstTaktEvent}
                        onChange={props.onChangeEvent}
                    >
                        {summary?.firstTaktEvent?.name || ""}
                    </MasterScheduleEventButtonModal>
                </div>
            </div>
        }

        return <div className={styles["date-cell"]} key={`${current.format(ShortTimeFormat)}-cell`} style={{alignContent: "baseline"}}>
            <div className={styles["date-area"]}>{current.date()}</div>
            {details?.map((d, ndx)=>
                (<DayEvent
                    key={`${current.format(ShortTimeFormat)}-${ndx}-day-event`}
                    current={current}
                    productionLine={d}
                    isReadOnly={readOnly}
                    onChangeEvent={props.onChangeEvent}
                    onChangeTruck={handleChangeTruck}
                />) )}
        </div>
    };



    const disabledDate = (date: Dayjs) => {
        // Disable dates not in the current month
        return !date.isSame(selectedDate, "month");
    };

    const headerRender = ({ value, onChange, onTypeChange }) => {
        const current = selectedDate;

        const prevMonth = () => {
            onChange(current.subtract(1, 'month'));
        };

        const nextMonth = () => {
            onChange(current.add(1, 'month'));
        };

        const onWeekChange = (month) => {
            const newValue = current.month(month);
            onChange(newValue);
        };

        const onYearChange = (year) => {
            const newValue = current.year(year);
            onChange(newValue);
        };

        return (

            <Row justify={"space-between"} style={{padding: "8px"}}>
                <style>
                    {`
              .date-switch .ant-select-selector {
                border-radius: 15px !important;
              }
            `}
                </style>
                <Col>
                    {lineWithEventsAsync?.isLoading && <Spin/>}
                </Col>
                <Col className={styles["date-switch"]}>
                    <Button type="text" shape="circle" rootClassName="month-switch" onClick={prevMonth} icon={<LeftOutlined />} />

                    {/* Month */}
                    <Select
                        value={current.month()}
                        onChange={onWeekChange}
                        style={{ width: 120, margin: '0 8px' }}
                        options={MONTHS.map((month, index) => {return {value: index, key: index, label: month}})}
                    >
                    </Select>

                    {/* Year */}
                    <Select
                        value={current.year()}
                        onChange={onYearChange}
                        style={{ width: 120, marginRight: '8px' }}
                        options={years.map((year) => {return {value: year, key: year, label: year}})}
                    />

                    <Button type="text" shape="circle" rootClassName="month-switch" onClick={nextMonth} icon={<RightOutlined />} />
                </Col>
            </Row>

        );
    };

    const leftPanelIconStyle = { fontSize: '150%'};
    const sidePanelStyle = (activeSidePanelKey && !isMobile)
        ? {
            flex: "3 1 0",
            paddingLeft: "1rem"
        }
        : { flex: "0" };

    return <div style={{display:"flex", flexDirection: "column"}}>

        <div style={{display:"flex", justifyContent:"space-between"}}>
            <div style={{flex:"auto"}}>
                <TruckReportFilterControls
                    filter={filter}
                    onFilterChange={handleFilterChange}
                    resetQueryParam={resetQueryParam}
                />
            </div>
            <div>
                <OptionsDropdown onChangeEvent={() => {
                    unscheduledTrucksAsync.mutate()
                    filteredTrucksAsync.mutate()
                    props.onChangeEvent?.();
                }} />
            </div>
        </div>

        <div style={{display:"flex"}}>
            <div style={{ flex: "7 1 20rem" }}>
                <Calendar
                    className={styles["month-calendar"]}
                    fullCellRender={cellRender}
                    onChange={onDateChange}
                    mode="month"
                    value={selectedDate}
                    headerRender={headerRender}
                    disabledDate={disabledDate}
                />
            </div>

            <div style={sidePanelStyle} >
                <SidePanel
                    isMobile={false}
                    activeKey={activeSidePanelKey}
                    onChange={handleTabClick}
                    items={[
                        {
                            key: "unscheduled",
                            label: <>
                                <TruckOutlined style={leftPanelIconStyle} />
                                <Title level={5}>Unscheduled</Title>
                            </>,
                            children:<UnscheduledTruckPanel unscheduledTrucks={unscheduledTrucksAsync.data?.content} onChange={handleChangeTruck} isReadOnly={props.isReadOnly} />
                        },
                    ]}
                />
            </div>
        </div>
    </div>
};


const UnscheduledTruckPanel= ( props: {
    unscheduledTrucks: TruckReportDto[] | undefined,
    onChange?: (t: Truck) => void,
    isReadOnly?: boolean
} ) => {

    const [selectedTruckSerialNumber, setSelectedTruckSerialNumber] = useState<string>();

    return <>
        {props.unscheduledTrucks?.map( t =>
            (<BMButton type="text" style={{width:'100%', padding:0}}
                       onClick={() => setSelectedTruckSerialNumber( t.serialNumber ) } >
                <SlotView
                    truck={t}
                    isReadOnly={props.isReadOnly} />
            </BMButton>))}

        <MSTruckScheduleModal serialNumber={selectedTruckSerialNumber} isReadOnly={props.isReadOnly}
                               onChange={(t) => {
                                   setSelectedTruckSerialNumber(undefined);
                                   props.onChange?.(t);
                               }}
                               onCancel={() => setSelectedTruckSerialNumber(undefined)} />
    </>

}

const DayEvent = (props: {
    current: Dayjs,
    productionLine: ProductionLineDetail | undefined
    onSelectDay?: (d: Dayjs) => void;
    onChangeEvent: (e: MasterScheduleEvent | undefined) => void;
    onChangeTruck?: (t: Truck) => void;
    isReadOnly: boolean;
}) => {

    const { current, productionLine } = props;

    const key = current.format("YYYYMMDD");

    const iceSlot = productionLine?.iceTaktRate || 0;
    const bevSlot = productionLine?.bevTaktRate || 0;

    const hasIce = !!iceSlot || !!productionLine?.iceSerials.length;
    const hasBev = !!bevSlot || !!productionLine?.bevSerials.length;

    return (
        <div key={`${key}-day-cell`} >
            <div style={{display: "flex", flexDirection: "column", fontSize: "24px", paddingTop: ".3rem", gap: "1rem"}}>

                {hasIce &&
                  <MotorFamilyLineView
                    current={current}
                    motorFamily={MotorFamily.ICE}
                    productionLine={productionLine}
                    isReadOnly={props.isReadOnly}
                    onChangeEvent={props.onChangeEvent}
                    onChangeTruck={props.onChangeTruck}
                  /> }

                {hasBev &&
                  <MotorFamilyLineView
                    current={current}
                    motorFamily={MotorFamily.BEV}
                    productionLine={productionLine}
                    isReadOnly={props.isReadOnly}
                    onChangeEvent={props.onChangeEvent}
                    onChangeTruck={props.onChangeTruck}
                  /> }

            </div>

        </div>
    );
}

export const MotorFamilyLineView = (props: {
    current: Dayjs,
    motorFamily: MotorFamily | undefined,
    productionLine: ProductionLineDetail | undefined,
    onSelectDay?: (d: Dayjs) => void;
    onChangeEvent?: (e: MasterScheduleEvent | undefined) => void;
    onChangeTruck?: (t: Truck) => void;
    isReadOnly?: boolean | undefined;
    filteredSerialNumbers?: string[];
}) => {


    const familyDetail = getMotorFamilyLineDetail(props.motorFamily, props.productionLine);
    const {family, line, serials, firstTaktEvent, taktRate }  = familyDetail || {};

    const [selectedTruck, setSelectedTruck] = useState<ScheduledTruck>();

    if (!props.productionLine) return <></>;

    const handleChangeTruck = (t: Truck) => {
        setSelectedTruck(undefined);
        props.onChangeTruck?.(t);
    }

    const slotCnt = Math.max(taktRate || 0, serials?.length || 0);
    const filteredSerials = props.filteredSerialNumbers;
    const key = props.current.format("YYYYMMDD");

    return <div style={{
        display: "flex",
        justifyContent: 'left',
        flexDirection: "column",
        gap: ".2rem",
        width: "100%",
        fontSize: "16px",
        textAlign: "left"
    }}
    >
        <MasterScheduleEventButtonModal
            type="text"
            style={{color: "inherit", padding: 0}}
            disabled={props.isReadOnly}
            startDate={props.current}
            event={firstTaktEvent}
            onChange={props.onChangeEvent}
        >
            <div style={{
                fontSize: "20px",
                lineHeight: "24px",
                textAlign: "center",
                borderRadius: "8px",
                marginBottom: "5px",
                width: "100%",
            }}>
                {Utils.snakeCaseToFirstLetterCapitalized(line) || "All Lines"} - {family?.toUpperCase()}
            </div>
        </MasterScheduleEventButtonModal>

        {slotCnt > 0 && Array.from({length: slotCnt }, (_, ndx) =>
            (<BMButton type="text"
                       style={{
                           width:'100%',
                           padding:0,
                           cursor: !props.onChangeTruck ? "auto": undefined
                       }}
                       disabled={!props.onChangeTruck}
                       onClick={() => setSelectedTruck( serials?.[ndx] ) }
                       key={`${key}-${props.motorFamily}-${ndx}-motor-family`}
            >
                <SlotView
                    current={props.current}
                    slotNdx={ndx}
                    truck={serials?.[ndx]}
                    lineDetail={familyDetail}
                    isReadOnly={props.isReadOnly || ((serials?.[ndx] && filteredSerials ) && !filteredSerials?.includes( serials?.[ndx].serialNumber ) ) } />
            </BMButton>))}

          <MSTruckScheduleModal slotNdx={selectedTruck?.productionSlot}
                                serialNumber={selectedTruck?.serialNumber}
                                current={props.current}
                                productionLine={props.productionLine}
                                isReadOnly={props.isReadOnly}
                                onChange={handleChangeTruck}
                                onCancel={() => setSelectedTruck(undefined)} />

    </div>
}

const OptionsDropdown = (props: {
    onChangeEvent: (e?:MasterScheduleEvent | undefined) => void;
}) => {

    const items : MenuItemType[] = [];
    items.push( {
        key:"schedule",
        label: <BulkScheduleButtonModal type={"text"}
                                        onChange={() => props.onChangeEvent()} >
            Schedule
        </BulkScheduleButtonModal>
    });

    items.push( {
        key:"unschedule",
        label: <BulkUnscheduleButtonModal type={"text"}
                                          onChange={() => props.onChangeEvent()} >
            Unschedule
        </BulkUnscheduleButtonModal>
    });

    return <Dropdown
        trigger={["click"]}
        menu={{items:items}}
    >
        {/* this div is to avoid a warning with strict mode */}
        <div>
            <BMButton
                type="primary"
                icon={<MoreOutlined/>}
            >Options</BMButton>
        </div>
    </Dropdown>

}


const BulkUnscheduleButtonModal = (props: Omit<BMButtonProps, "id" | "value" | "onChange" > & {
    onChange?:(d?:TruckReportDto[] | undefined) => void
    onCancel?:() => void
    open?:boolean
}) => {

    const {onChange: b, open: c, ...btnProps} = props;

    const { filteredTrucks } = useMasterScheduleSlotsContext();

    const configurator = useContext(ConfiguratorContext);
    const intl = useIntl();

    const [isSaving, setIsSaving] = useState<boolean>();
    const [isOpen, setIsOpen] = useState<boolean>(props.open || false);

    const unscheduleTruck = async (truckId: number ) => {
        setIsSaving(true);
        try {
            const resp = await configurator.api.updateTruck( truckId, {
                productionLine: null,
                productionSlot: null,
                productionDate: null,
                scheduledShipDate: null,
                estReadyToShip: null,
            } );
            return resp.data;
        }
        catch(e:any) {
            const id = e.response?.data?.message || e.message ;
            const errorMsg = intl.formatMessage({ id });
            notification.error( { message: "Failed to update truck. " + errorMsg });
        }
        setIsSaving(false);

        return;
    }

    const handleOk = async () => {

        if ( filteredTrucks?.length ) {
            for (const item of filteredTrucks) {
                await unscheduleTruck(item.id);
            }
        }

        props.onChange?.();
        setIsOpen(false);
    }
    const handleCancel = () => {
        setIsOpen(false);
    }

    return <>
        <BMButton {...btnProps}
                  onClick={() => setIsOpen(true)}
                  disabled={!filteredTrucks?.length}
                  onDisabledClick={() => {
                      notification.error( { message: "A least one vehicle must be highlighted." });
                  }} />
        <Modal
            open={isOpen}
            width={"50rem"}
            onCancel={handleCancel}
            onClose={handleCancel}
            onOk={handleOk}
            confirmLoading={isSaving}
            afterOpenChange={(open) => {
                if (open) {
                }
            }}>
            <Result
                status="warning"
                title="The following highlighted vehicles will be unscheduled."
                style={{padding: "0"}}
            >

                <Table
                    columns={[
                        {
                            title: "Truck",
                            dataIndex: "serialNumber",
                        },
                        {
                            title: "Production Date",
                            dataIndex: "estProductionDate",
                        },
                        {
                            title: "Customer",
                            dataIndex: "customer",
                        },
                        {
                            title: "Dealer",
                            dataIndex: "dealer",
                        },
                    ]}
                    rowKey="id"
                    size="small"
                    dataSource={filteredTrucks}
                    scroll={{x: true}}
                />

            </Result>

        </Modal>
    </>

}

interface SaveTruckDto {
    productionLine?: ProductionLine | undefined | null
    productionSlot?: number | undefined | null
    productionDate?: Date | undefined | null
    scheduledShipDate?: Date | undefined | null
    estReadyToShip?: Date | undefined | null
}

const BulkScheduleButtonModal = (props: Omit<BMButtonProps, "id" | "value" | "onChange" > & {
    onChange?:(d?:TruckReportDto[] | undefined) => void
    onCancel?:() => void
    open?:boolean
}) => {

    const {onChange: b, open: c, ...btnProps} = props;

    const { filteredTrucks, getProductionLineDetail } = useMasterScheduleSlotsContext();

    const configurator = useContext(ConfiguratorContext);
    const intl = useIntl();

    const [isSaving, setIsSaving] = useState<boolean>();
    const [isOpen, setIsOpen] = useState<boolean>();
    const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs());

    const saveTruck = async (truckId: number, dto:SaveTruckDto ) => {
        try {
            const resp = await configurator.api.updateTruck( truckId, dto);
            return resp.data;
        }
        catch(e:any) {
            const id = e.response?.data?.message || e.message ;
            const errorMsg = intl.formatMessage({ id });
            notification.error( { message: "Failed to update truck. " + errorMsg });
        }

        return;
    }

    const handleOk = async () => {
        if ( filteredTrucks?.length ) {
            try {
                await scheduleTrucks(filteredTrucks);
            }
            catch(e:any) {
                const id = e.response?.data?.message || e.message ;
                const errorMsg = intl.formatMessage({ id });
                notification.error( { message: "Failed to update truck. " + errorMsg });
            }
        }

        props.onChange?.();

        setIsOpen(false);
    }
    const handleCancel = () => {
        setIsOpen(false);
    }

    const MAX_SEARCH_MONTHS =  3;
    const MAX_SEARCH = 30 * MAX_SEARCH_MONTHS;
    const scheduleTrucks = async (trucks:TruckReportDto[]) => {

        const queue : Record<number, SaveTruckDto> = {};

        setIsSaving(true);
        for (const truck of trucks) {

            const resp = await configurator.api.fetchSelectionInfo({truckId: truck.id});

            const fuelType = resp.data?.filter(a => a.category?.categoryId === FUEL_TYPE_CATEGORY_ID_STR  )
                .map(a => a.bomDescription ).find(v=>v)

            let slot = 1;
            let line;
            let maxCnt = 0;
            let next = selectedStartDate.subtract(1, "day");
            while ( true ) {
                next = next.add(1, "day");
                if ( maxCnt++ > MAX_SEARCH ) {
                    notification.error( { message: `Failed to find an open slot before ${next.format("dddd, MMMM Do YYYY")} months.` });
                    return;
                }

                if ( isWeekend(next) ) continue;

                line = findOpenLine(next, fuelType);

                //check for conflict
                const productionLine = getProductionLineDetail?.(next, line);
                const motorFamilyDetail = getMotorFamilyLineDetail(fuelType, productionLine);

                const scheduled = Object.values(queue).filter(q => dayjs(q.productionDate).isSame(next) && q.productionLine == line);
                if ( ( motorFamilyDetail?.lineOpen || 0 ) < scheduled.length + 1) continue;

                slot = (motorFamilyDetail?.serials.length || 0) + 1;

                if ( line ) break;
            }


            queue[truck.id] = {
                productionDate: next.toDate(),
                productionLine: line,
                productionSlot: slot,
                estReadyToShip: next?.add( DEFAULT_RTS_OFFSET, 'day').toDate(),
                scheduledShipDate: next?.add( DEFAULT_RTS_OFFSET + DEFAULT_RTS_SHIP_OFFSET, 'day').toDate()
            };
        }

        await Promise.all(
            Object.keys(queue).map( (id) => saveTruck(Number(id), queue[id]) ) )

        setIsSaving(false);
    };

    const findOpenLine = (next:Dayjs, fuelType) => {
        return  Object.values(ProductionLine).find( l => !isFull(next, l, fuelType) )
    }

    const isFull = (next:Dayjs, line: ProductionLine, fuelType) => {
        const nextLineDetail = getProductionLineDetail?.(next, line);
        const motorFamilyDetail = getMotorFamilyLineDetail( fuelType, nextLineDetail )
        const lineOpen = motorFamilyDetail?.lineOpen || 0;

        return lineOpen <= 0;
    }
    const isWeekend = (next:Dayjs) => {
        const weekday = next.weekday();
        return (weekday === 6) || (weekday  === 0); // 6 = Saturday, 0 = Sunday
    }


    return <>
        <BMButton {...btnProps}
                  onClick={() => setIsOpen(true)}
                  disabled={!filteredTrucks?.length}
                  onDisabledClick={() => {
                      notification.error( { message: "A least one vehicle must be highlighted." });
                  }} />
        <Modal
            open={isOpen}
            width={"50rem"}
            onCancel={handleCancel}
            onClose={handleCancel}
            onOk={handleOk}
            confirmLoading={isSaving}
            afterOpenChange={(open) => {
                if (open) {
                }
            }}>
            <Result
                status="warning"
                title="The following highlighted vehicles will be scheduled in the next available opening."
                style={{padding: "0"}}
            >

                <div style={{marginBottom: "1rem"}} >
                    <Descriptions
                        column={2}
                        className={descriptionStyles['details-description']}
                        size="small"
                        items={[
                            {
                                label: "Starting Date",
                                children:
                                    <DatePicker
                                        value={selectedStartDate}
                                        onChange={setSelectedStartDate}
                                    />
                            },
                        ]} />
                </div>

                <Table
                    columns={[
                        {
                            title: "Truck",
                            dataIndex: "serialNumber",
                        },
                        {
                            title: "Production Date",
                            dataIndex: "estProductionDate",
                        },
                        {
                            title: "Customer",
                            dataIndex: "customer",
                        },
                        {
                            title: "Dealer",
                            dataIndex: "dealer",
                        },
                    ]}
                    rowKey="id"
                    size="small"
                    dataSource={filteredTrucks}
                    scroll={{x: true}}
                />

            </Result>

        </Modal>
    </>

}

export default MSSlotCalendar;
